import React, { useRef, useEffect, useState } from 'react';
import { modelUrl, posterUrl, verifyUrl } from '../lib/storage.js';

// model-viewer is loaded globally via index.html

/**
 * ModelViewer component for displaying 3D GLB models with built-in auto-fit functionality
 * 
 * Features:
 * - Built-in auto-fit using bounds="tight" and camera-target="auto"
 * - Automatic camera distance calculation with camera-orbit="... auto"
 * - Guaranteed full model visibility with no cropping
 * - Instant positioning with jumpCameraToGoal() on load
 * - Custom orientation support for models with unusual pivots
 * 
 * @param {string} src - Legacy URL fallback for backward compatibility
 * @param {string} posterUrl - URL for poster image shown while loading
 * @param {string} modelBucket - Supabase bucket name for the model
 * @param {string} modelKey - Object key/path in the bucket
 * @param {string} orientation - Optional camera orientation override (e.g., "0deg 45deg 150%")
 * @param {number} fovDeg - Field of view in degrees (default: 25)
 * @param {function} onLoad - Callback when model loads successfully
 * @param {function} onError - Callback when model fails to load
 * @param {string} className - CSS classes for the container
 */
export default function ModelViewer({ 
  src, 
  posterUrl = null,
  modelBucket = null,
  modelKey = null,
  orientation = "0deg 75deg 130%", // Default camera position - close view for good visibility
  fovDeg = 35, // Field of view in degrees - wider for better framing
  onLoad, 
  onError,
  className = "w-full h-full"
}) {
  const ref = useRef(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urlVerified, setUrlVerified] = useState(false);
  
  // Handle null orientation - use default if null/undefined
  const finalOrientation = orientation || "0deg 75deg 130%";
  const finalFov = fovDeg || 35;

  // Generate signed URL when component mounts or parameters change
  useEffect(() => {
    const generateSignedUrl = async () => {
      // Prefer bucket/key over src URL
      const bucket = modelBucket;
      const key = modelKey;
      
      if (!bucket || !key) {
        // Fallback to src URL for backward compatibility
        if (!src) return;
        
        try {
          setIsLoading(true);
          setError(null);
          setUrlVerified(false);
          
          console.log('🔗 ModelViewer: Using legacy src URL:', src);
          
          // If it's already a full URL, use it directly
          if (src.startsWith('http')) {
            await verifyUrl(src);
            setSignedUrl(src);
            setUrlVerified(true);
            return;
          }
          
          // Try to extract bucket and key from URL
          const urlParts = src.split('/storage/v1/object/');
          if (urlParts.length < 2) {
            throw new Error('Invalid storage URL format');
          }
          
          const path = urlParts[1];
          const parts = path.split('/');
          const bucketName = parts[0];
          const objectKey = parts.slice(1).join('/');
          
          const generatedUrl = await modelUrl(bucketName, objectKey);
          await verifyUrl(generatedUrl);
          
          setSignedUrl(generatedUrl);
          setUrlVerified(true);
          
        } catch (err) {
          console.error('❌ ModelViewer: Failed to process src URL:', err);
          setError(err.message);
          if (onError) onError(err);
        } finally {
          setIsLoading(false);
        }
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        setUrlVerified(false);
        
        console.log('🔗 ModelViewer: Generating URL for bucket:', bucket, 'key:', key);
        
        const generatedUrl = await modelUrl(bucket, key);
        
        console.log('✅ ModelViewer: URL generated:', generatedUrl);
        console.log('🔗 ModelViewer: You can test this URL directly by opening it in a new tab');
        
        // Verify URL is accessible
        await verifyUrl(generatedUrl);
        console.log('✅ ModelViewer: URL verified via HEAD check');
        
        setSignedUrl(generatedUrl);
        setUrlVerified(true);
        
      } catch (err) {
        console.error('❌ ModelViewer: Failed to generate/verify URL:', err);
        setError(err.message);
        if (onError) onError(err);
      } finally {
        setIsLoading(false);
      }
    };

    generateSignedUrl();
  }, [src, modelBucket, modelKey, onError]);

  // Handle model load event with auto-fit
  const handleLoad = (e) => {
    console.log('✅ model-viewer load complete');
    
    // Apply auto-fit immediately using built-in functionality
    try {
      const mv = e.target;
      if (mv.jumpCameraToGoal) {
        mv.jumpCameraToGoal();
        console.log('✅ Applied auto-fit camera positioning with jumpCameraToGoal()');
      }
      
      // Apply custom orientation if provided (after auto-fit)
      if (orientation && !orientation.includes('auto')) {
        mv.setAttribute('camera-orbit', orientation);
        console.log('✅ Applied custom orientation:', orientation);
      }
    } catch (err) {
      console.warn('⚠️ Could not apply camera positioning:', err);
    }
    
    setIsLoading(false);
    if (onLoad) onLoad();
  };

  // Set up model-viewer event listeners
  useEffect(() => {
    const modelViewer = ref.current;
    if (!modelViewer || !signedUrl) return;

    const handleError = (e) => {
      console.error('❌ model-viewer error:', e?.detail || e);
      setError('Failed to load 3D model');
      setIsLoading(false);
      if (onError) onError(e);
    };

    const handleProgress = (e) => {
      const progress = e.detail.totalProgress;
      console.log(`📊 model-viewer progress: ${(progress * 100).toFixed(1)}%`);
    };

    modelViewer.addEventListener('error', handleError);
    modelViewer.addEventListener('progress', handleProgress);

    return () => {
      modelViewer.removeEventListener('error', handleError);
      modelViewer.removeEventListener('progress', handleProgress);
    };
  }, [signedUrl, onError]);

  if (error) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-800 rounded-xl`}>
        <div className="text-center text-white p-6">
          <div className="text-6xl mb-4">⚠️</div>
          <div className="text-xl font-semibold mb-2">Could not load 3D model</div>
          <div className="text-sm text-gray-400 mb-4">{error}</div>
          <div className="flex gap-3 justify-center">
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
            >
              Retry
            </button>
            {signedUrl && (
              <button 
                onClick={() => window.open(signedUrl, '_blank')}
                className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Test URL
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !signedUrl || !urlVerified) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-800 rounded-xl`}>
        <div className="text-center text-white p-6">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-lg font-medium mb-2">Loading 3D Model</div>
          <div className="text-sm text-gray-400">
            {!signedUrl ? 'Generating secure URL...' : 
             !urlVerified ? 'Verifying access...' : 'Loading model...'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <model-viewer
        ref={ref}
        src={signedUrl}
        poster={posterUrl}
        style={{ 
          width: '100%', 
          height: className.includes('modal') ? (window.innerWidth < 768 ? '400px' : '500px') : '70vh',
          minHeight: window.innerWidth < 768 ? '300px' : '400px',
          backgroundColor: '#1a1a1a',
          borderRadius: '12px',
          display: 'block',
          margin: '0 auto'
        }}
        camera-controls
        touch-action="pan-y"
        shadow-intensity="1"
        autoplay
        loading="eager"
        bounds="tight"
        camera-target="auto"
        camera-orbit={finalOrientation}
        min-camera-orbit="auto auto 100%"
        max-camera-orbit="auto auto 500%"
        field-of-view={`${finalFov}deg`}
        max-field-of-view="45deg"
        ar
        ar-modes="webxr scene-viewer quick-look"
        ios-src={signedUrl}
        alt="3D Model"
        onLoad={handleLoad}
      />
    </div>
  );
}
